import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import { API_BASE_URL } from '../../config/api';
import { useCurrency } from '../../context/CurrencyContext';
import './AdminDashboard2Page.css';

const AdminDashboard2Page = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[now.getMonth()]}, ${now.getFullYear()}`;
  });
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: '...',
    totalProducts: '...',
    totalProjects: '...',
    totalNews: '...'
  });
  const [earnings, setEarnings] = useState({
    ads: '...',
    subscription: '...',
    membership: '...'
  });
  const [revenue, setRevenue] = useState({
    currentMonth: '...',
    newSuppliers: '...',
    newSpecialists: '...'
  });
  const [supplierRequests, setSupplierRequests] = useState([]);
  const [productRequests, setProductRequests] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAllSuppliers, setSelectAllSuppliers] = useState(false);
  const [selectAllProducts, setSelectAllProducts] = useState(false);
  const [chartData, setChartData] = useState([]);

  // Month options
  const months = [
    'Jan, 2025', 'Feb, 2025', 'Mar, 2025', 'Apr, 2025', 'May, 2025', 'Jun, 2025',
    'Jul, 2025', 'Aug, 2025', 'Sep, 2025', 'Oct, 2025', 'Nov, 2025', 'Dec, 2025',
    'Jan, 2026', 'Feb, 2026', 'Mar, 2026', 'Apr, 2026', 'May, 2026', 'Jun, 2026',
    'Jul, 2026', 'Aug, 2026', 'Sep, 2026', 'Oct, 2026', 'Nov, 2026', 'Dec, 2026'
  ];

  const fetchDashboardData = useCallback(async () => {
    console.log('=== FETCHING DASHBOARD DATA ===');
    setLoading(true);
    try {
      const [monthStr, yearStr] = selectedMonth.split(', ');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIndex = monthNames.indexOf(monthStr);
      const year = parseInt(yearStr);
      const cacheBuster = Date.now(); // Prevent browser caching

      const apiUrl = `${API_BASE_URL}/dashboard/admin/stats?month=${monthIndex + 1}&year=${year}&_t=${cacheBuster}`;
      console.log('Calling API:', apiUrl);
      
      const [statsRes, suppliersRes, productsRes] = await Promise.all([
        fetch(apiUrl),
        fetch(`${API_BASE_URL}/dashboard/admin/supplier-requests?month=${monthIndex + 1}&year=${year}&_t=${cacheBuster}`),
        fetch(`${API_BASE_URL}/dashboard/admin/product-requests?month=${monthIndex + 1}&year=${year}&_t=${cacheBuster}`)
      ]);

      console.log('Stats Response Status:', statsRes.status);
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        console.log('Dashboard API Response:', data);
        const newStats = {
          totalUsers: data.users?.total || 0,
          totalProducts: data.products?.total || 0,
          totalProjects: data.projects || 0,
          totalNews: data.news || 0
        };
        console.log('Setting stats to:', newStats);
        setStats(newStats);
        setEarnings({
          ads: data.revenue?.ads || 0,
          subscription: data.revenue?.subscription || 0,
          membership: data.revenue?.membership || 0
        });
        const monthlyData = data.monthly || data.today || {};
        setRevenue({
          currentMonth: data.revenue?.currentMonth || 0,
          newSuppliers: monthlyData.newSuppliers || 0,
          newSpecialists: monthlyData.newSpecialists || 0
        });
        if (Array.isArray(data.chartData)) {
          setChartData(data.chartData);
        }
      } else {
        console.error('Stats API failed:', statsRes.status, await statsRes.text());
      }

      if (suppliersRes.ok) {
        const data = await suppliersRes.json();
        if (Array.isArray(data)) setSupplierRequests(data);
      }

      if (productsRes.ok) {
        const data = await productsRes.json();
        if (Array.isArray(data)) setProductRequests(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Handle supplier checkbox
  const handleSupplierSelect = (id) => {
    const strId = String(id);
    setSelectedSuppliers(prev => 
      prev.includes(strId) ? prev.filter(i => i !== strId) : [...prev, strId]
    );
  };

  // Handle product checkbox
  const handleProductSelect = (id) => {
    const strId = String(id);
    setSelectedProducts(prev => 
      prev.includes(strId) ? prev.filter(i => i !== strId) : [...prev, strId]
    );
  };

  // Handle select all suppliers
  const handleSelectAllSuppliers = () => {
    if (selectAllSuppliers) {
      setSelectedSuppliers([]);
    } else {
      setSelectedSuppliers(supplierRequests.map(s => String(s.id)));
    }
    setSelectAllSuppliers(!selectAllSuppliers);
  };

  // Handle select all products
  const handleSelectAllProducts = () => {
    if (selectAllProducts) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(productRequests.map(p => String(p.id)));
    }
    setSelectAllProducts(!selectAllProducts);
  };

  // Bulk approve suppliers
  const handleBulkApproveSuppliers = async () => {
    if (selectedSuppliers.length === 0) {
      alert('Please select at least one supplier to approve');
      return;
    }
    if (!window.confirm(`Approve ${selectedSuppliers.length} supplier(s)?`)) return;
    
    try {
      let success = 0, fail = 0;
      for (const id of selectedSuppliers) {
        const supplierId = String(id).replace('seller-', '');
        const res = await fetch(`${API_BASE_URL}/sellers/${supplierId}/approve`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
        if (res.ok) success++; else fail++;
      }
      alert(`Approved: ${success}, Failed: ${fail}`);
      setSelectedSuppliers([]);
      setSelectAllSuppliers(false);
      fetchDashboardData();
    } catch (error) {
      alert('Error approving suppliers');
    }
  };

  // Bulk decline suppliers
  const handleBulkDeclineSuppliers = async () => {
    if (selectedSuppliers.length === 0) {
      alert('Please select at least one supplier to decline');
      return;
    }
    if (!window.confirm(`Decline ${selectedSuppliers.length} supplier(s)?`)) return;
    
    try {
      let success = 0, fail = 0;
      for (const id of selectedSuppliers) {
        const supplierId = String(id).replace('seller-', '');
        const res = await fetch(`${API_BASE_URL}/sellers/${supplierId}/decline`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
        if (res.ok) success++; else fail++;
      }
      alert(`Declined: ${success}, Failed: ${fail}`);
      setSelectedSuppliers([]);
      setSelectAllSuppliers(false);
      fetchDashboardData();
    } catch (error) {
      alert('Error declining suppliers');
    }
  };

  // Bulk approve products
  const handleBulkApproveProducts = async () => {
    if (selectedProducts.length === 0) {
      alert('Please select at least one product to approve');
      return;
    }
    if (!window.confirm(`Approve ${selectedProducts.length} product(s)?`)) return;
    
    try {
      let success = 0, fail = 0;
      for (const id of selectedProducts) {
        const res = await fetch(`${API_BASE_URL}/products/${id}/approve`, { method: 'PATCH' });
        if (res.ok) success++; else fail++;
      }
      alert(`Approved: ${success}, Failed: ${fail}`);
      setSelectedProducts([]);
      setSelectAllProducts(false);
      fetchDashboardData();
    } catch (error) {
      alert('Error approving products');
    }
  };

  // Bulk decline products
  const handleBulkDeclineProducts = async () => {
    if (selectedProducts.length === 0) {
      alert('Please select at least one product to decline');
      return;
    }
    if (!window.confirm(`Decline ${selectedProducts.length} product(s)?`)) return;
    
    try {
      let success = 0, fail = 0;
      for (const id of selectedProducts) {
        const res = await fetch(`${API_BASE_URL}/products/${id}/decline`, { method: 'PATCH' });
        if (res.ok) success++; else fail++;
      }
      alert(`Declined: ${success}, Failed: ${fail}`);
      setSelectedProducts([]);
      setSelectAllProducts(false);
      fetchDashboardData();
    } catch (error) {
      alert('Error declining products');
    }
  };

  // Approve single product
  const handleApproveProduct = async (product) => {
    try {
      const res = await fetch(`${API_BASE_URL}/products/${product.id}/approve`, { method: 'PATCH' });
      if (res.ok) {
        alert('Product approved!');
        fetchDashboardData();
      } else {
        alert('Failed to approve product');
      }
    } catch (error) {
      alert('Error approving product');
    }
  };

  return (
    <AdminLayout>
      <div className="admin-dashboard2">
        {/* Page Header */}
        <div className="ad2-page-header">
          <h1 className="ad2-page-title">Admin Dashboard</h1>
          <p className="ad2-page-subtitle">Centralized control to manage users, content, and platform operations.</p>
        </div>

        {/* Stats Cards */}
        <div className="ad2-stats-grid">
          <div className="ad2-stat-card">
            <div className="ad2-stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="ad2-stat-content">
              <span className="ad2-stat-label">Total Users</span>
              <span className="ad2-stat-value">{stats.totalUsers}</span>
            </div>
          </div>

          <div className="ad2-stat-card">
            <div className="ad2-stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="ad2-stat-content">
              <span className="ad2-stat-label">Total Product Listings</span>
              <span className="ad2-stat-value">{stats.totalProducts}</span>
            </div>
          </div>

          <div className="ad2-stat-card">
            <div className="ad2-stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 11l3 3L22 4" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="ad2-stat-content">
              <span className="ad2-stat-label">Total Project Listing</span>
              <span className="ad2-stat-value">{stats.totalProjects}</span>
            </div>
          </div>

          <div className="ad2-stat-card">
            <div className="ad2-stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M19 20H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v1m2 13a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="ad2-stat-content">
              <span className="ad2-stat-label">Total News Listing</span>
              <span className="ad2-stat-value">{stats.totalNews}</span>
            </div>
          </div>
        </div>

        {/* Dashboard Chart Section */}
        <div className="ad2-dashboard-section">
          {/* Earnings Cards - moved inside Dashboard section */}
          <div className="ad2-earnings-cards">
            <div className="ad2-earning-card">
              <div className="ad2-earning-icon blue">
                <span>$</span>
              </div>
              <div className="ad2-earning-content">
                <span className="ad2-earning-label">Earning from Ads</span>
                <span className="ad2-earning-value">${earnings.ads}</span>
              </div>
            </div>

            <div className="ad2-earning-card">
              <div className="ad2-earning-icon orange">
                <span>$</span>
              </div>
              <div className="ad2-earning-content">
                <span className="ad2-earning-label">Earning from subscription</span>
                <span className="ad2-earning-value">${earnings.subscription}</span>
              </div>
            </div>

            <div className="ad2-earning-card">
              <div className="ad2-earning-icon green">
                <span>$</span>
              </div>
              <div className="ad2-earning-content">
                <span className="ad2-earning-label">Earning from Membership fee</span>
                <span className="ad2-earning-value">${earnings.membership}</span>
              </div>
            </div>
          </div>

          <div className="ad2-dashboard-header">
            <div>
              <h2 className="ad2-section-title">Dashboard</h2>
              <p className="ad2-section-subtitle">Overview of {selectedMonth}</p>
            </div>
            <div className="ad2-month-selector">
              <span className="ad2-sort-label">Sort by</span>
              <select 
                value={selectedMonth} 
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="ad2-month-select"
              >
                {months.map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="ad2-dashboard-content">
            <div className="ad2-revenue-box">
              <h3 className="ad2-revenue-amount">${revenue.currentMonth}</h3>
              <p className="ad2-revenue-label">Current Month Revenue</p>
              <div className="ad2-stats-row">
                <div className="ad2-stat-item">
                  <span className="ad2-stat-number">{revenue.newSuppliers}</span>
                  <span className="ad2-stat-text">New Suppliers Join</span>
                </div>
                <div className="ad2-stat-item">
                  <span className="ad2-stat-number">{revenue.newSpecialists}</span>
                  <span className="ad2-stat-text">New Specialists Join</span>
                </div>
              </div>
            </div>
            
            <div className="ad2-chart-placeholder">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={chartData.length > 0 ? chartData : [
                  {month:'Jan',membership:0,ads:0,verification:0},
                  {month:'Feb',membership:0,ads:0,verification:0},
                  {month:'Mar',membership:0,ads:0,verification:0},
                  {month:'Apr',membership:0,ads:0,verification:0},
                  {month:'May',membership:0,ads:0,verification:0},
                  {month:'Jun',membership:0,ads:0,verification:0},
                  {month:'Jul',membership:0,ads:0,verification:0},
                  {month:'Aug',membership:0,ads:0,verification:0},
                  {month:'Sep',membership:0,ads:0,verification:0},
                  {month:'Oct',membership:0,ads:0,verification:0},
                  {month:'Nov',membership:0,ads:0,verification:0},
                  {month:'Dec',membership:0,ads:0,verification:0}
                ]} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="ad2MembershipGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.05}/>
                    </linearGradient>
                    <linearGradient id="ad2AdsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#79C889" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#79C889" stopOpacity={0.05}/>
                    </linearGradient>
                    <linearGradient id="ad2VerificationGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#E8AE48" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#E8AE48" stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="month" tick={{ fill: '#636F85', fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#636F85', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `$${(v/1000).toFixed(1)}k` : `$${v}`} width={50} />
                  <Tooltip
                    contentStyle={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 13 }}
                    formatter={(value, name) => [`$${value.toLocaleString()}`, name]}
                  />
                  <Area type="monotone" dataKey="membership" name="Membership" stroke="#3B82F6" strokeWidth={2.5} fill="url(#ad2MembershipGrad)" dot={{ r: 3, fill: '#3B82F6' }} activeDot={{ r: 5 }} />
                  <Area type="monotone" dataKey="ads" name="Ads" stroke="#79C889" strokeWidth={2} fill="url(#ad2AdsGrad)" dot={false} />
                  <Area type="monotone" dataKey="verification" name="Verification" stroke="#E8AE48" strokeWidth={2} fill="url(#ad2VerificationGrad)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Tables Section */}
        <div className="ad2-tables-section">
          {/* Supplier Requests Table */}
          <div className="ad2-table-container">
            <div className="ad2-table-header">
              <h3 className="ad2-table-title">Today's New Supplier & Specialists Request</h3>
              <div className="ad2-table-actions">
                <span className="ad2-see-all" onClick={() => navigate('/admin/suppliers')}>See All</span>
              </div>
            </div>
            {loading ? (
              <p style={{padding: '20px', textAlign: 'center'}}>Loading...</p>
            ) : supplierRequests.length === 0 ? (
              <p style={{padding: '20px', textAlign: 'center', color: '#666'}}>No supplier requests for this period</p>
            ) : (
              <table className="ad2-table">
                <thead>
                  <tr>
                    <th>
                      <input 
                        type="checkbox" 
                        checked={selectAllSuppliers}
                        onChange={handleSelectAllSuppliers}
                      />
                    </th>
                    <th>Logo</th>
                    <th>Creation date</th>
                    <th>Name</th>
                    <th>Company Type</th>
                    <th>User Type</th>
                    <th>Location</th>
                    <th>Email</th>
                    <th>Membership Plan</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {supplierRequests.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <input 
                          type="checkbox" 
                          checked={selectedSuppliers.includes(String(item.id))}
                          onChange={() => handleSupplierSelect(item.id)}
                        />
                      </td>
                      <td>
                        <span className="ad2-logo-icon">
                          {item.logo ? <img src={item.logo} alt="" style={{width: 32, height: 32, borderRadius: '50%'}} /> : '🏢'}
                        </span>
                      </td>
                      <td>{item.date || item.creationDate || (item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '-')}</td>
                      <td>{item.name || item.companyName}</td>
                      <td>{item.type || item.companyType || item.category || '-'}</td>
                      <td>{item.userType || 'Supplier'}</td>
                      <td>{item.location || item.country || '-'}</td>
                      <td>{item.email || '-'}</td>
                      <td>
                        <span className={`ad2-membership-badge ${(item.plan || item.membershipPlan || 'free').toLowerCase()}`}>
                          {item.plan || item.membershipPlan || 'Free'}
                        </span>
                      </td>
                      <td>
                        <span 
                          className="ad2-action-link" 
                          onClick={() => navigate(`/admin/suppliers/${item.id}`)}
                        >
                          Details
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Product Requests Table */}
          <div className="ad2-table-container">
            <div className="ad2-table-header">
              <h3 className="ad2-table-title">Today's Product Request</h3>
              <div className="ad2-table-actions">
                <span className="ad2-see-all" onClick={() => navigate('/admin/products')}>See All</span>
              </div>
            </div>
            {loading ? (
              <p style={{padding: '20px', textAlign: 'center'}}>Loading...</p>
            ) : productRequests.length === 0 ? (
              <p style={{padding: '20px', textAlign: 'center', color: '#666'}}>No product requests for this period</p>
            ) : (
              <table className="ad2-table">
                <thead>
                  <tr>
                    <th>
                      <input 
                        type="checkbox" 
                        checked={selectAllProducts}
                        onChange={handleSelectAllProducts}
                      />
                    </th>
                    <th>Product</th>
                    <th>Company/Business</th>
                    <th>Price</th>
                    <th>Membership Plan</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {productRequests.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <input 
                          type="checkbox" 
                          checked={selectedProducts.includes(String(item.id))}
                          onChange={() => handleProductSelect(item.id)}
                        />
                      </td>
                      <td>
                        <div className="ad2-product-cell">
                          <span className="ad2-product-icon">
                            {item.image ? <img src={item.image} alt="" style={{width: 40, height: 40, objectFit: 'cover', borderRadius: 4}} /> : '📦'}
                          </span>
                          <span>{item.name || item.productData}</span>
                        </div>
                      </td>
                      <td>{item.company || item.seller?.companyName || '-'}</td>
                      <td>{formatPrice(item.price || 0)}</td>
                      <td>
                        <span className={`ad2-membership-badge ${(item.membershipPlan || 'free').toLowerCase()}`}>
                          {item.membershipPlan || 'Free'}
                        </span>
                      </td>
                      <td className="ad2-action-cell">
                        <button 
                          className="ad2-approve-btn"
                          onClick={() => handleApproveProduct(item)}
                        >
                          Approve
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard2Page;
